// Stock Adjustment (Bahan Baku) — Phase 1D-δ-2
// List + create dialog + detail dialog dengan approval flow (DRAFT → APPROVED/REJECTED/CANCELLED)
//
// Pola daftar seragam (28 Sep 2026; `docs/POLA_DAFTAR.md`): mode SERVER penuh, keadaan di alamat halaman,
// saringan bawaan = "Belum selesai" (draf menunggu persetujuan) + gudang sendiri (Admin Gudang); chip bisa
// dihapus. Dialog buat/rincian MENAMBAH `?create`/`?id` ke alamat — posisi daftar tidak hilang saat ditutup.
// Pilihan bahan lewat `PemilihCari` (dulu seluruh master bahan dipindai ke browser).
import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { api } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { SlidersHorizontal, Plus, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { fetchStockReasons } from "../../lib/stockReasons";
import { formatDateTime } from "../../lib/format";
import { InvAdjustmentStatusBadge, INV_ADJUSTMENT_STATUS_TONE } from "../../components/etams/StatusBadge";
import {
  listAdjustments, createAdjustment, getAdjustment,
  approveAdjustment, rejectAdjustment, cancelAdjustment,
  getMaterial, apiErrorMessage,
} from "../../lib/inventoryApi";
import Hint from "../../components/etams/Hint";
import { useDaftar } from "../../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../../components/etams/PalangDaftar";
import PemilihCari from "../../components/etams/PemilihCari";
import { cariBahan, labelBahan } from "../../lib/sumberPilihan";

// Arah pergerakan stok → kata sehari-hari (bukan kode IN/OUT).
const ARAH = { IN: "Masuk", OUT: "Keluar" };
const arahAdj = (a) => ARAH[a.type] || (Number(a.qty_change) > 0 ? "Masuk" : "Keluar");

// KONTRAS-01 Batch 9 — peta `STATUS_BADGE` lokal yang dulu di sini (4 status
// dengan `cls:` warna keras) pindah ke `INV_ADJUSTMENT_STATUS_TONE` di
// `components/etams/StatusBadge.jsx`. Label tidak berubah sepotong pun.
const LABEL_STATUS = { DRAFT: "Draf", APPROVED: "Disetujui", REJECTED: "Ditolak", CANCELLED: "Dibatalkan" };
const LABEL_SARING = { BELUM_SELESAI: "Belum selesai (draf)", ...LABEL_STATUS };
//: Cermin `URUT_SAH_ADJ` di backend/services/inventory_service.py.
const URUT_SAH = ["created_at", "qty_change", "status"];
const KOLOM_CSV = [
  { kunci: "bahan", judul: "Bahan", ambil: (a) => labelBahan(a.material) },
  { kunci: "gudang", judul: "Gudang", ambil: (a) => a.store?.name || "" },
  { kunci: "arah", judul: "Tipe", ambil: arahAdj },
  { kunci: "qty_change", judul: "Perubahan" },
  { kunci: "status", judul: "Status", ambil: (a) => INV_ADJUSTMENT_STATUS_TONE[a.status]?.l || LABEL_STATUS[a.status] || a.status },
  { kunci: "reason", judul: "Alasan" },
  { kunci: "created_at", judul: "Tanggal", ambil: (a) => formatDateTime(a.created_at) },
];

/** Tambah/hapus kunci dialog di alamat TANPA membuang keadaan daftar (h, q, saringan). */
function ubahParamDialog(setSp, ubah) {
  setSp((prev) => {
    const n = new URLSearchParams(prev);
    ["create", "id", "wizard", "production_in"].forEach((k) => n.delete(k));
    Object.entries(ubah || {}).forEach(([k, v]) => n.set(k, v));
    return n;
  }, { replace: true });
}

export default function Adjustments() {
  const { roleCode, profile } = useAuth();
  const [sp, setSp] = useSearchParams();
  const selectedId = sp.get("id");
  const showCreate = sp.get("create") === "1";

  const canWrite = ["OWNER", "KEPALA_CABANG", "ADMIN_GUDANG"].includes(roleCode);
  const canApprove = ["OWNER", "KEPALA_CABANG"].includes(roleCode);
  const gudangSaya = (roleCode === "ADMIN_GUDANG" && profile?.store_id) || "";

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/stores").then(r => setStores(r.data || []))
      .catch((e) => toast.error(apiErrorMessage(e, "Daftar gudang gagal dimuat.")));
  }, []);

  const daftar = useDaftar(items, {
    server: { total, urutSah: URUT_SAH },
    urutAwal: { kunci: "created_at", arah: "desc" },
    saringan: {
      status: { label: "Status", param: "status", bawaan: "BELUM_SELESAI", tampil: (v) => LABEL_SARING[v] || v },
      gudang: { label: "Gudang", param: "store_id", bawaan: gudangSaya,
                tampil: (v) => stores.find(s => s.id === v)?.name || "…" },
    },
  });
  const { cari, limit, offset, sort, arah } = daftar.paramServer;
  const { paramSaringan } = daftar;
  const bangunParams = useCallback((o = {}) => ({
    ...paramSaringan, ...(cari && { q: cari }), limit, offset, sort, arah, ...o,
  }), [paramSaringan, cari, limit, offset, sort, arah]);
  const ambilHalaman = useCallback(async (off, n) =>
    (await listAdjustments(bangunParams({ offset: off, limit: n }))).data, [bangunParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listAdjustments(bangunParams());
      setItems(r.data.items || []);
      setTotal(r.data.total ?? 0);
    } catch (e) { toast.error(apiErrorMessage(e, "Gagal memuat adjustment.")); }
    finally { setLoading(false); }
  }, [bangunParams]);

  useEffect(() => { load(); }, [load]);
  const tutupDialog = useCallback((r) => { ubahParamDialog(setSp, {}); if (r) load(); }, [setSp, load]);

  return (
    <div className="space-y-6" data-testid="adjust-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <SlidersHorizontal className="h-6 w-6" /> Adjustment Stok Bahan
            <Hint>Koreksi manual stok bahan baku; stok bergerak saat adjustment disetujui. Pembuatnya tidak boleh menyetujui sendiri.</Hint>
          </h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" asChild data-testid="adjust-ke-barang-berkurang">
            <Link to="/inventory/barang-berkurang">Persetujuan barang berkurang</Link>
          </Button>
          <Button variant="outline" size="sm" onClick={load}>
            <RefreshCw className="h-4 w-4 mr-1" /> Muat ulang
          </Button>
          {canWrite && (
            <Button size="sm" onClick={() => ubahParamDialog(setSp, { create: "1" })} data-testid="adjust-create-btn">
              <Plus className="h-4 w-4 mr-1" /> Adjustment Baru
            </Button>
          )}
        </div>
      </div>

      <PalangDaftar daftar={daftar} placeholder="Cari kode / nama bahan / alasan" testId="adjust-palang"
                    ekspor={{ nama: "adjustment_stok_bahan", kolom: KOLOM_CSV, ambilHalaman }}>
        <select value={daftar.nilaiSaringan.status} onChange={(e) => daftar.setSaringan("status", e.target.value)}
                className="h-9 rounded border px-2 text-sm" data-testid="adjust-status-filter" aria-label="Saring status">
          <option value="">Semua status</option>
          {Object.entries(LABEL_SARING).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={daftar.nilaiSaringan.gudang} onChange={(e) => daftar.setSaringan("gudang", e.target.value)}
                className="h-9 rounded border px-2 text-sm" data-testid="adjust-store-filter" aria-label="Saring gudang">
          <option value="">Semua Gudang</option>
          {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </PalangDaftar>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b">
              <tr className="text-left">
                <th className="px-4 py-3 font-medium">Bahan</th>
                <th className="px-4 py-3 font-medium">Gudang</th>
                <th className="px-4 py-3 font-medium">Tipe</th>
                <ThUrut daftar={daftar} kunci="qty_change" className="px-4 py-3 font-medium" align="right">Perubahan</ThUrut>
                <ThUrut daftar={daftar} kunci="status" className="px-4 py-3 font-medium">Status</ThUrut>
                <th className="px-4 py-3 font-medium">Alasan</th>
                <ThUrut daftar={daftar} kunci="created_at" className="px-4 py-3 font-medium">Tanggal</ThUrut>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {loading && daftar.tampil.length === 0 && (
                <tr><td colSpan="8" className="p-8 text-center text-muted-foreground">Memuat...</td></tr>
              )}
              {!loading && daftar.tampil.length === 0 && (
                <tr><td colSpan="8" className="p-12 text-center text-muted-foreground text-sm" data-testid="adjust-kosong">
                  {daftar.cari.trim() || daftar.saringanAktif.length
                    ? "Tidak ada adjustment yang cocok dengan pencarian atau saringan ini."
                    : `Belum ada adjustment.${canWrite ? " Klik 'Adjustment Baru' untuk mulai." : ""}`}
                </td></tr>
              )}
              {daftar.tampil.map(a => (
                <tr key={a.id} className="border-b hover:bg-muted/50">
                  <td className="px-4 py-3 text-xs">
                    <span className="font-mono">{a.material?.code || "—"}</span>
                    {a.material?.name && <span className="block text-muted-foreground">{a.material.name}</span>}
                  </td>
                  <td className="px-4 py-3">{a.store?.name || "—"}</td>
                  <td className="px-4 py-3">
                    <Badge variant="outline">{arahAdj(a)}</Badge>
                  </td>
                  <td className={`px-4 py-3 text-right font-semibold ${Number(a.qty_change) < 0 ? "text-danger-soft-foreground" : "text-success-soft-foreground"}`}>
                    {Number(a.qty_change) > 0 ? "+" : ""}{a.qty_change}
                  </td>
                  <td className="px-4 py-3"><InvAdjustmentStatusBadge value={a.status} /></td>
                  <td className="px-4 py-3 text-muted-foreground max-w-xs truncate">{a.reason || "-"}</td>
                  <td className="px-4 py-3 text-xs">{formatDateTime(a.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="ghost" size="sm" onClick={() => ubahParamDialog(setSp, { id: a.id })}
                            data-testid={`adjust-detail-${a.id}`}>Detail</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Paginasi daftar={daftar} testId="adjust-paginasi" />
      </Card>

      {showCreate && <CreateDialog onClose={tutupDialog} />}
      {selectedId && (
        <DetailDialog id={selectedId} canApprove={canApprove} canCancel={canWrite}
                      profileId={profile?.id}
                      onClose={tutupDialog} />
      )}
    </div>
  );
}

function CreateDialog({ onClose }) {
  const [bahan, setBahan] = useState(null);
  const [stores, setStores] = useState([]);
  // Batch 2.3: alasan terstruktur dari master. SENGAJA kosong di awal —
  // tidak ada pilihan terpasang otomatis, supaya pengguna memilih sadar.
  const [reasons, setReasons] = useState([]);
  const [form, setForm] = useState({
    material_id: "", store_id: "", qty_change: 1, reason_code: "",
    reason: "", override_reason: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/stores").then(r => r.data || []),
      fetchStockReasons("manual_adjustment"),
    ]).then(([s, rs]) => { setStores(s); setReasons(rs); })
      .catch((e) => toast.error(apiErrorMessage(e, "Data pendukung gagal dimuat.")));
  }, []);

  const submit = async () => {
    if (!form.material_id || !form.store_id) { toast.error("Bahan dan gudang wajib dipilih."); return; }
    if (!form.qty_change || form.qty_change === 0) { toast.error("Perubahan jumlah tidak boleh 0."); return; }
    if (!form.reason_code) {
      toast.error("Alasan stok wajib dipilih — sistem tidak menebak."); return;
    }
    if (!form.reason.trim() || form.reason.trim().length < 5) {
      toast.error("Alasan wajib diisi (min 5 karakter)."); return;
    }
    setSaving(true);
    try {
      const payload = {
        material_id: form.material_id, store_id: form.store_id,
        qty_change: Number(form.qty_change), reason: form.reason.trim(),
        reason_code: form.reason_code,
      };
      if (form.override_reason.trim()) payload.override_reason = form.override_reason.trim();
      await createAdjustment(payload);
      toast.success("Adjustment dibuat sebagai draf, menunggu persetujuan.");
      onClose(true);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="max-w-lg" data-testid="adjust-create-dialog">
        <DialogHeader><DialogTitle>Adjustment Baru</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="block">
            <span className="text-sm">Bahan *</span>
            <PemilihCari value={bahan} onChange={(m) => { setBahan(m); setForm({ ...form, material_id: m?.id || "" }); }}
                         cari={cariBahan} label={labelBahan} placeholder="— Pilih —" className="w-full"
                         testId="adjust-material" />
          </div>
          <label className="block">
            <span className="text-sm">Gudang *</span>
            <select value={form.store_id}
                    onChange={(e) => setForm({ ...form, store_id: e.target.value })}
                    className="w-full h-9 rounded border px-2 text-sm" data-testid="adjust-store">
              <option value="">— Pilih —</option>
              {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-sm">Perubahan jumlah (positif = masuk, negatif = keluar) *</span>
            <Input type="number" step="0.001" value={form.qty_change}
                   onChange={(e) => setForm({ ...form, qty_change: e.target.value })}
                   data-testid="adjust-qty" />
          </label>
          <label className="block">
            <span className="text-sm">Alasan stok *</span>
            <select value={form.reason_code}
                    onChange={(e) => setForm({ ...form, reason_code: e.target.value })}
                    className="w-full h-9 rounded border px-2 text-sm"
                    data-testid="adjust-reason-code">
              <option value="">— Pilih alasan —</option>
              {reasons.map(r => (
                <option key={r.code} value={r.code}>{r.name}</option>
              ))}
            </select>
            <span className="text-xs text-muted-foreground">
              Menentukan perlakuan biaya: Rusak/Hilang dihitung sebagai
              kerugian, bukan pemakaian normal.
            </span>
          </label>
          <label className="block">
            <span className="text-sm">Keterangan (min 5 karakter) *</span>
            <Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
                      rows={2} data-testid="adjust-reason" />
          </label>
          <label className="block">
            <span className="text-sm text-muted-foreground">Alasan pengecualian Owner (opsional — bila jumlah bisa membuat stok negatif)</span>
            <Textarea value={form.override_reason}
                      onChange={(e) => setForm({ ...form, override_reason: e.target.value })}
                      rows={2} placeholder="Kosongkan jika tidak perlu" data-testid="adjust-override" />
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)}>Batal</Button>
          <Button onClick={submit} disabled={saving} data-testid="adjust-submit">
            {saving ? "Menyimpan..." : "Buat Draf"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DetailDialog({ id, canApprove, canCancel, profileId, onClose }) {
  const [a, setA] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rejectNotes, setRejectNotes] = useState("");
  const [cancelReason, setCancelReason] = useState("");
  const [overrideReason, setOverrideReason] = useState("");
  const [showReject, setShowReject] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [showApprove, setShowApprove] = useState(false);
  // Nama bahan & gudang untuk tampilan (bukan potongan ID teknis).
  const [namaBahan, setNamaBahan] = useState("");
  const [namaGudang, setNamaGudang] = useState("");

  const load = useCallback(async () => {
    try { const r = await getAdjustment(id); setA(r.data); }
    catch (e) { toast.error(apiErrorMessage(e)); onClose(false); }
  }, [id, onClose]);

  useEffect(() => {
    if (!a) return;
    if (a.material_id) {
      getMaterial(a.material_id)
        .then((r) => setNamaBahan(r.data ? `${r.data.code || ""} — ${r.data.name || ""}` : ""))
        .catch((e) => toast.error(apiErrorMessage(e, "Nama bahan gagal dimuat.")));
    }
    if (a.store_id) {
      api.get("/stores")
        .then((r) => setNamaGudang((r.data || []).find((s) => s.id === a.store_id)?.name || ""))
        .catch((e) => toast.error(apiErrorMessage(e, "Nama gudang gagal dimuat.")));
    }
  }, [a]);

  useEffect(() => { load(); }, [load]);

  const doApprove = async () => {
    setBusy(true);
    try {
      const body = overrideReason.trim() ? { override_reason: overrideReason.trim() } : {};
      await approveAdjustment(id, body);
      toast.success("Adjustment disetujui; stok sudah tercatat.");
      onClose(true);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setBusy(false); }
  };

  const doReject = async () => {
    if (rejectNotes.trim().length < 5) { toast.error("Alasan tolak minimal 5 karakter."); return; }
    setBusy(true);
    try {
      await rejectAdjustment(id, { rejection_notes: rejectNotes.trim() });
      toast.success("Adjustment ditolak.");
      onClose(true);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setBusy(false); }
  };

  const doCancel = async () => {
    if (cancelReason.trim().length < 5) { toast.error("Alasan batal minimal 5 karakter."); return; }
    setBusy(true);
    try {
      await cancelAdjustment(id, { reason: cancelReason.trim() });
      toast.success("Adjustment dibatalkan.");
      onClose(true);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setBusy(false); }
  };

  if (!a) return (
    <Dialog open onOpenChange={() => onClose(false)}>
      <DialogContent><div className="p-8 text-center text-muted-foreground">Memuat...</div></DialogContent>
    </Dialog>
  );

  const isSelf = a.requested_by === profileId || a.created_by === profileId;
  const canApproveThis = canApprove && a.status === "DRAFT" && !isSelf;
  const requesterCantApproveWarning = canApprove && a.status === "DRAFT" && isSelf;
  const canCancelThis = canCancel && a.status === "DRAFT";

  return (
    <Dialog open onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-2xl" data-testid="adjust-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {a.adjustment_number || "Adjustment"} <InvAdjustmentStatusBadge value={a.status} />
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-2">
            <div><span className="text-muted-foreground">Bahan:</span> {namaBahan || "—"}</div>
            <div><span className="text-muted-foreground">Gudang:</span> {namaGudang || "—"}</div>
            <div><span className="text-muted-foreground">Perubahan:</span> <span className={`font-semibold ${Number(a.qty_change) < 0 ? "text-danger-soft-foreground" : "text-success-soft-foreground"}`}>
              {Number(a.qty_change) > 0 ? "+" : ""}{a.qty_change}</span></div>
            <div><span className="text-muted-foreground">Tipe:</span> {arahAdj(a)}</div>
            <div><span className="text-muted-foreground">Dibuat:</span> {formatDateTime(a.created_at)}</div>
            <div><span className="text-muted-foreground">Diproses:</span> {formatDateTime(a.approved_at || a.rejected_at || a.cancelled_at)}</div>
          </div>
          <div><span className="text-muted-foreground">Alasan:</span> {a.reason}</div>
          {a.override_reason && (
            <div className="bg-warning-soft text-warning-soft-foreground border border-warning/40 rounded p-2 text-xs">
              <span className="font-medium">Pengecualian Owner:</span> {a.override_reason}
            </div>
          )}
          {a.rejection_notes && (
            <div className="bg-danger-soft text-danger-soft-foreground border border-danger/30 rounded p-2">
              <span className="font-medium">Alasan Tolak:</span> {a.rejection_notes}
            </div>
          )}
          {a.cancel_reason && (
            <div className="bg-muted/50 border border-border rounded p-2">
              <span className="font-medium">Alasan Batal:</span> {a.cancel_reason}
            </div>
          )}
          {a.stock_ledger_id && (
            <div className="text-xs text-muted-foreground">Stok sudah tercatat di kartu stok.</div>
          )}
          {requesterCantApproveWarning && (
            <div className="text-xs bg-warning-soft text-warning-soft-foreground border border-warning/40 rounded p-2">
              ⚠️ Anda pembuat adjustment ini; persetujuan harus dari orang lain (Owner/Kepala Cabang selain Anda).
            </div>
          )}
        </div>
        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" onClick={() => onClose(false)}>Tutup</Button>
          {canApproveThis && !showApprove && !showReject && !showCancel && (
            <>
              <Button onClick={() => setShowApprove(true)}
                      data-testid="adjust-approve-open">Setujui</Button>
              <Button variant="destructive" onClick={() => setShowReject(true)}
                      data-testid="adjust-reject-open">Tolak</Button>
            </>
          )}
          {canCancelThis && !showApprove && !showReject && !showCancel && (
            <Button variant="outline" onClick={() => setShowCancel(true)}
                    data-testid="adjust-cancel-open">Batalkan</Button>
          )}
          {showApprove && (
            <div className="w-full space-y-2 pt-2 border-t">
              <div className="text-xs text-muted-foreground">
                Alasan pengecualian (opsional) — isi jika jumlah akan membuat stok negatif, khusus Owner.
              </div>
              <Textarea placeholder="Alasan pengecualian (kosongkan jika tidak perlu)..."
                        value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)}
                        rows={2} data-testid="adjust-approve-override" />
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowApprove(false)}>Kembali</Button>
                <Button size="sm" onClick={doApprove} disabled={busy}
                        data-testid="adjust-approve-confirm">Konfirmasi Setujui</Button>
              </div>
            </div>
          )}
          {showReject && (
            <div className="w-full space-y-2 pt-2 border-t">
              <Textarea placeholder="Alasan tolak (min 5 karakter)..."
                        value={rejectNotes} onChange={(e) => setRejectNotes(e.target.value)}
                        rows={2} data-testid="adjust-reject-notes" />
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowReject(false)}>Kembali</Button>
                <Button variant="destructive" size="sm" onClick={doReject} disabled={busy}
                        data-testid="adjust-reject-confirm">Konfirmasi Tolak</Button>
              </div>
            </div>
          )}
          {showCancel && (
            <div className="w-full space-y-2 pt-2 border-t">
              <Textarea placeholder="Alasan batal (min 5 karakter)..."
                        value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}
                        rows={2} data-testid="adjust-cancel-reason" />
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowCancel(false)}>Kembali</Button>
                <Button variant="destructive" size="sm" onClick={doCancel} disabled={busy}
                        data-testid="adjust-cancel-confirm">Konfirmasi Batal</Button>
              </div>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
